import React, { useEffect, useState } from 'react';
import T from 'Components/Helpers/translate';
import { Button } from 'App/shadcn/ui/button';
import { Input } from 'App/shadcn/ui/input';
import { CreditsPill, LINK, UsesCredits } from './LookupParts';
import { findCompanyPeople, STATUS_KEYS, type CompanyPeopleResult, type CompanyPerson } from './lookupApi';

type Search = { role: string; people: CompanyPerson[]; nextCursor: string | null; aiCredits: number };
type State =
	| { state: 'idle' }
	| { state: 'loading'; search: Search | null }
	| { state: 'done'; status: CompanyPeopleResult['status']; search: Search };

/** A finished search, kept by a surface that unmounts this list but should not pay for it twice. */
export type CompanyPeopleMemory = { role: string; state: Extract<State, { state: 'done' }> };

const MESSAGES: Record<Exclude<CompanyPeopleResult['status'], 'found'>, string> = {
	notFound: 'prospectingTools.lookup.people.notFound',
	notAvailable: 'prospectingTools.lookup.people.notAvailable',
	outOfCredits: STATUS_KEYS.outOfCredits,
	failed: STATUS_KEYS.failed
};

type Props = {
	domain?: string;
	/**
	 * The company's LinkedIn page, where the rep is on it: searched instead of the domain, which also
	 * matches companies whose website is hosted under it (an Upsales Pages page is upsales.com, WEB-13771).
	 */
	linkedinCompanyUrl?: string;
	/** Looks a person up by their LinkedIn profile: email, phone, and Add to CRM. */
	onLookUp: (query: string) => void;
	/** The per-person action's label; Lookup's "Find email and phone number" unless a surface says otherwise. */
	actionLabel?: string;
	/** The last finished search to show again, and where to keep each new one (WEB-13771). */
	remembered?: CompanyPeopleMemory;
	onRemember?: (memory: CompanyPeopleMemory) => void;
};

/**
 * "People at this company" on a company Lookup result (WEB-13377): who works there now, by role,
 * for any company. Profiles only, 25 at a time; a rep looks up the person they want, so the email
 * and phone are bought only for them. Shown under AIRSCALE_INTEGRATION; the server answers
 * `notAvailable` when the admin turned Airscale off or the account is EU ONLY.
 */
const CompanyPeople = ({ domain, linkedinCompanyUrl, onLookUp, actionLabel, remembered, onRemember }: Props) => {
	const [role, setRole] = useState(remembered?.role ?? '');
	const [state, setState] = useState<State>(remembered?.state ?? { state: 'idle' });

	// Each finished search is kept, so coming back to this company shows it without paying again.
	useEffect(() => {
		if (state.state === 'done') onRemember?.({ role: state.search.role, state });
	}, [state, onRemember]);
	const search = state.state === 'idle' ? null : state.search;

	const run = (more: boolean) => {
		const current = more ? search : null;
		const asked = more && current ? current.role : role.trim();
		setState({ state: 'loading', search: current });
		findCompanyPeople({
			domain,
			linkedinCompanyUrl,
			role: asked || undefined,
			cursor: current?.nextCursor ?? undefined
		})
			.then(result =>
				setState({
					state: 'done',
					status: result.status,
					search: {
						role: asked,
						people: [...(current?.people ?? []), ...result.people],
						nextCursor: result.nextCursor,
						aiCredits: (current?.aiCredits ?? 0) + result.aiCredits
					}
				})
			)
			.catch((err: unknown) => {
				console.error('[Lookup] people search failed', err);
				setState({
					state: 'done',
					status: 'failed',
					search: current ?? { role: asked, people: [], nextCursor: null, aiCredits: 0 }
				});
			});
	};

	const loading = state.state === 'loading';
	const message = state.state === 'done' && state.status !== 'found' ? MESSAGES[state.status] : null;

	return (
		<section
			aria-label={T('prospectingTools.lookup.people.title')}
			className="flex flex-col border-t border-grey-4"
		>
			<h3 className="px-5 pb-2 pt-4 text-body-md font-semibold text-brand">
				{T('prospectingTools.lookup.people.title')}
			</h3>
			<div className="px-5 pb-2">
				<UsesCredits />
			</div>
			<form
				className="flex gap-2 px-5 pb-3"
				onSubmit={event => {
					event.preventDefault();
					run(false);
				}}
			>
				<Input
					value={role}
					onChange={event => setRole(event.target.value)}
					placeholder={T('prospectingTools.lookup.people.rolePlaceholder')}
					aria-label={T('default.role')}
					className="min-w-0 flex-1"
				/>
				<Button type="submit" variant="outline" disabled={loading}>
					{T('prospectingTools.lookup.people.search')}
				</Button>
			</form>
			{search?.people.length ? (
				<div className="flex items-center justify-between gap-3 px-5 pb-2 text-body-sm text-grey-11">
					<span>{T('prospectingTools.lookup.people.count', { count: search.people.length })}</span>
					{search.aiCredits > 0 ? <CreditsPill credits={search.aiCredits} /> : null}
				</div>
			) : null}
			{search?.people.length ? (
				<ul className="m-0 list-none p-0">
					{search.people.map(person => (
						<li
							key={person.linkedinUrl}
							className="flex items-center gap-3 border-t border-grey-2 px-5 py-3 text-body-md"
						>
							<span className="min-w-0 flex-1">
								<span className="block font-medium text-grey-13">{person.name}</span>
								{person.title ? <span className="block text-grey-11">{person.title}</span> : null}
							</span>
							<Button
								type="button"
								variant="outline"
								size="sm"
								onClick={() => onLookUp(person.linkedinUrl)}
							>
								{actionLabel ?? T('prospectingTools.lookup.findContactDetails')}
							</Button>
						</li>
					))}
				</ul>
			) : null}
			<div className="flex flex-wrap items-center gap-3 px-5 pb-4 pt-2 text-body-sm text-grey-11">
				{loading ? <span role="status">{T('prospectingTools.lookup.loading')}</span> : null}
				{message ? <span role="status">{T(message)}</span> : null}
				{search?.nextCursor && !loading ? (
					<button type="button" className={LINK} onClick={() => run(true)}>
						{T('default.showMore')}
					</button>
				) : null}
			</div>
		</section>
	);
};

export default CompanyPeople;
